import FixaCarousel from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import { useImageCarouselAccessibility } from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/useImageCarouselAccessibility";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaTheatre } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaTheatre";
import { useTranslation } from "@coworker/locales";
import {
  cloneElement,
  ComponentProps,
  ComponentType,
  ReactElement,
  useState,
} from "react";
import { ZoomableImage } from "../ZoomableImage/ZoomableImage";

interface ViewerImage {
  src: string;
  alt: string;
  rotation: number;
}
export interface ViewerImageObject {
  src?: string;
  url?: string;
  alt?: string;
  fileName?: string;
  rotation?: number;
}
export type ViewerImageInput = string | ViewerImageObject;

const normalizeViewerImages = (images: ViewerImageInput[]): ViewerImage[] =>
  images.map((image, index) => {
    const fallbackAlt = `Image ${index + 1}`;

    if (typeof image === "string") {
      return { src: image, alt: fallbackAlt, rotation: 0 };
    }

    return {
      src: image.src ?? image.url ?? "",
      alt: image.alt ?? image.fileName ?? fallbackAlt,
      rotation: image.rotation ?? 0,
    };
  });

type ZoomableImageProps = ComponentProps<typeof ZoomableImage>;

interface ImageViewerProps {
  images: ViewerImageInput[];
  isOpen: boolean;
  onClose: () => void;
  carouselId: string;
  ZoomableImageComponent?: ComponentType<ZoomableImageProps>;
  renderWrapper?: (imageElement: ReactElement, key: string) => ReactElement;
  slideIndex?: number;
}

const generateImageKey = (
  index: number,
  isVisible: boolean,
  currentIndex: number
) => `zoom-${index}-viewed-${isVisible ? currentIndex : "hidden"}`;

export const ImageViewer = ({
  images,
  isOpen,
  onClose,
  carouselId,
  ZoomableImageComponent = ZoomableImage,
  renderWrapper,
  slideIndex,
}: ImageViewerProps) => {
  const { t } = useTranslation();
  const { slideshowAccessibilityProps } = useImageCarouselAccessibility();
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleVisibleSlideChange = (indices: number[]) => {
    if (indices.length > 0 && indices[0] !== undefined) {
      setCurrentIndex(indices[0]);
    }
  };

  if (!isOpen) return null;

  const renderImage = (image: ViewerImage, index: number) => {
    const isVisible = currentIndex === index;
    const key = generateImageKey(index, isVisible, currentIndex);

    const imageElement = (
      <ZoomableImageComponent
        src={image.src}
        alt={image.alt}
        rotation={image.rotation}
        fallback={!image.src || image.src.trim() === ""}
      />
    );

    return renderWrapper
      ? renderWrapper(imageElement, key)
      : cloneElement(imageElement, { key });
  };

  return (
    <FixaModal visible={isOpen} handleCloseBtn={onClose}>
      <FixaTheatre
        header={<FixaModalHeader />}
        ariaLabel={t("imageCarouselString")}
      >
        <FixaCarousel
          {...slideshowAccessibilityProps}
          id={carouselId}
          data-testid={carouselId}
          isSlideShow
          hasNoControls={false}
          onVisibleSlideChange={handleVisibleSlideChange}
          slideIndex={slideIndex}
        >
          {normalizeViewerImages(images).map(renderImage)}
        </FixaCarousel>
      </FixaTheatre>
    </FixaModal>
  );
};
